import React from 'react';
import { Group, Rect } from 'react-konva';
import useSelectionRectangleOperations from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/hooks';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useSurfaceNodeMovedOnSurface from 'components/canvas/components/nodes/components/base-node/hooks/use-surface-node-moved-on-surface';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';
import useSelectedNodesWithPermissionToMakeAnAction from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/hooks/use-selected-nodes-with-permission-to-make-an-action';
import DeleteIcon from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/components/delete-icon';
import ContextBar from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/components/context-bar';
import WithCursor from 'components/canvas/components/with-cursor';
import CopyIcon from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/components/copy-icon';
import CutIcon from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/components/cut-icon';
import updateSurfaceNodePosition from 'graphql/surface-node/mutations/update-surface-node-position';
import { notEmpty } from 'utils';
import { throttle } from 'lodash';
import { SurfaceNodeType } from 'graphql/types';
import { color } from 'styles/settings';
import { PROTOCOL_PADDING } from 'components/canvas/components/nodes/components/protocol-node';

interface SelectionRectangleProps {
  surface: string;
}

const padding = 25;

export const SELECTION_GROUP_ID = 'selection-rectangle';

function SelectionRectangle({ surface }: SelectionRectangleProps) {
  const {
    snappedDimensions,
    cutNodes,
    copyNodes,
    deleteNodes,
  } = useSelectionRectangleOperations(surface);

  const {
    selectedNodesAllowedToReposition,
  } = useSelectedNodesWithPermissionToMakeAnAction(surface);

  const { x, y, width, height } = snappedDimensions;

  const { findShapeById } = useCanvas();

  const surfaceNodeMovedOnSurface = useSurfaceNodeMovedOnSurface();

  let moveOperationIndex = 0;

  const {
    canCopySurfaceNode,
    canCutSurfaceNode,
    canDeleteAny,
  } = useCurrentSurfacePermissions();

  const fetchNodeFromCacheById = useFetchNodeFromCacheById(surface);

  const icons = [
    canCopySurfaceNode() && <CopyIcon onClick={() => copyNodes()} />,
    canCutSurfaceNode() && <CutIcon onClick={() => cutNodes()} />,
    canDeleteAny && <DeleteIcon onClick={deleteNodes} />,
  ];

  const selectedNodesShapesThatAreAllowedToBeRepositioned = React.useMemo(() => {
    return selectedNodesAllowedToReposition.map(({ id }) => findShapeById(id));
  }, [selectedNodesAllowedToReposition]);

  // We exclude protocol containing nodes from selectedNodes array because they are in protocol. But here we have to move them manually because of containing node cloned shapes of protocol references. See src/components/canvas/components/nodes/components/protocol-node/index.tsx:1

  const selectedProtocolContainingNodeShapesMap = selectedNodesAllowedToReposition
    .filter(
      //@ts-ignore
      (node) => node.type === SurfaceNodeType.Protocol && node?.contains,
    )
    .reduce((map, node) => {
      // Check if reference
      if (node.isReference) {
        return {
          ...map,
          // If reference is on the same surface there is cloned shape with same id as containing node in the original. Which means we cannot fetch it by id. There is another field added to cloned shape attrs...which is fromProtocol.
          [node.id]: findShapeById((shape) => {
            if (shape.attrs.fromProtocol === node.id) {
              return true;
            }
            //@ts-ignore
            return shape.attrs.id === node.contains.id;
          }),
        };
      }
      return {
        ...map,
        //@ts-ignore
        [node.id]: findShapeById(node?.contains?.id),
      };
    }, {});

  const oldX = React.useRef(0);
  const oldY = React.useRef(0);

  function onDragStart(event) {
    moveOperationIndex = 0;
    oldX.current = event.target.x();
    oldY.current = event.target.y();
  }

  function onDragEnd() {
    moveOperationIndex = 0;
    // Update position on server
    selectedNodesShapesThatAreAllowedToBeRepositioned.map((nodeShape) => {
      // We need to fetch latest node data from cache to preserve added/removed links because node prop can be stale
      const position = nodeShape?.position();

      if (position) {
        updateSurfaceNodePosition({
          input: { position, nodeId: nodeShape.attrs.id },
        });
      }
    });
  }

  const onDragMove = throttle((event) => {
    const diffX = event.target.x() - oldX.current;
    const diffY = event.target.y() - oldY.current;

    selectedNodesShapesThatAreAllowedToBeRepositioned.forEach((nodeShape) => {
      nodeShape.x(nodeShape.x() + diffX);
      nodeShape.y(nodeShape.y() + diffY);

      if (nodeShape.attrs.type === SurfaceNodeType.Protocol) {
        //@ts-ignore
        const containingShape =
          selectedProtocolContainingNodeShapesMap[nodeShape.attrs.id];

        const cloneShape = findShapeById((shape) => {
          return shape.attrs.fromProtocol === nodeShape.attrs.id;
        });

        if (cloneShape || containingShape) {
          const shape = cloneShape || containingShape;
          shape.x(nodeShape.x() + PROTOCOL_PADDING);
          shape.y(nodeShape.y() + PROTOCOL_PADDING);
        }
      }

      surfaceNodeMovedOnSurface({
        id: nodeShape.attrs.id,
        surface,
        position: nodeShape.position(),
        index: moveOperationIndex++,
      });
    });

    oldX.current = event.target.x();
    oldY.current = event.target.y();
  }, 50);

  if (notEmpty(snappedDimensions)) {
    return (
      <Group
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        draggable>
        <Group x={x + width / 2} y={y - padding / 2}>
          <ContextBar icons={icons} />
        </Group>
        <WithCursor type="grabbing">
          <Rect
            id={SELECTION_GROUP_ID}
            x={x - padding}
            y={y - padding}
            width={width + padding * 2}
            height={height + padding * 2}
            stroke={color.brand.blueLight}
            stroke-width="2px"
          />
        </WithCursor>
      </Group>
    );
  }

  return null;
}

export default SelectionRectangle;
